// Fork: public detail page for one published incident.
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { StatusHeader } from '../../components/status/StatusHeader'
import {
  LEVEL_BAR_CLASS,
  LEVEL_LABEL,
  formatDateTime,
  formatSpan,
  incidentStateLabel,
  isResolved,
  timeAgo,
  useStatusBranding,
  useStatusHref,
  type StatusIncidentDetail,
} from '../../components/status/statusShared'

const HEADER_BG = { resolved: 'bg-green-50', degraded: 'bg-yellow-50', outage: 'bg-red-50' }
const HEADER_BORDER = { resolved: 'border-green-300', degraded: 'border-yellow-300', outage: 'border-red-300' }

const DOT_CLASS = { resolved: 'bg-green-500', degraded: 'bg-yellow-400', outage: 'bg-red-500' }
const LINE_CLASS = { resolved: 'bg-green-500', degraded: 'bg-yellow-400', outage: 'bg-red-400' }

function tone(incident: StatusIncidentDetail, label?: string): keyof typeof DOT_CLASS {
  if (label?.toLowerCase() === 'resolved' || (label === undefined && isResolved(incident))) return 'resolved'
  return incident.level === 'outage' ? 'outage' : 'degraded'
}

function AffectedComponents({ incident }: { incident: StatusIncidentDetail }) {
  if (incident.components.length === 0) return null
  const end = incident.resolved_at
  return (
    <section className="bg-white border border-gray-200 rounded-lg">
      <h2 className="px-4 sm:px-6 py-4 text-base font-semibold text-gray-900 border-b border-gray-100">Affected components</h2>
      <div className="px-4 sm:px-6 py-2 flex justify-between gap-4 text-xs text-gray-500 border-b border-gray-100">
        <span>{formatDateTime(incident.triggered_at)}</span>
        <span>{end ? formatDateTime(end) : 'Ongoing'}</span>
      </div>
      <ul className="px-4 sm:px-6 py-3 space-y-3">
        {incident.components.map(component => (
          <li key={component.key}>
            <p className="text-sm text-gray-900">
              {component.name} <span className="text-gray-500">· {LEVEL_LABEL[component.level]}</span>
            </p>
            <div className="mt-1.5 flex gap-1 h-3" aria-hidden="true">
              <span className={`w-[8%] rounded-sm ${LEVEL_BAR_CLASS.operational}`} />
              <span className={`flex-1 rounded-sm ${LEVEL_BAR_CLASS[component.level]}`} />
              {end && <span className={`w-[8%] rounded-sm ${LEVEL_BAR_CLASS.operational}`} />}
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function Updates({ incident }: { incident: StatusIncidentDetail }) {
  if (incident.updates.length === 0) return null
  return (
    <section id="updates" className="bg-white border border-gray-200 rounded-lg">
      <h2 className="px-4 sm:px-6 py-4 text-base font-semibold text-gray-900 border-b border-gray-100">Updates</h2>
      <ol className="px-4 sm:px-6 py-4">
        {incident.updates.map((update, index) => {
          const newer = incident.updates[index - 1]
          const updateTone = tone(incident, update.label ?? '')
          const isLast = index === incident.updates.length - 1
          return (
            <li key={`${update.created_at}-${index}`} className="relative pl-6 pb-6 last:pb-0">
              <span className={`absolute left-0 top-1.5 w-2 h-2 rounded-full ${DOT_CLASS[updateTone]}`} aria-hidden="true" />
              {!isLast && <span className={`absolute left-[3px] top-4 bottom-0 w-0.5 ${LINE_CLASS[updateTone]}`} aria-hidden="true" />}
              <h3 className="text-sm font-semibold text-gray-900">{update.label || 'Update'}</h3>
              <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap break-words">{update.message}</p>
              <p className="mt-1 text-xs text-gray-500">
                <time dateTime={update.created_at}>{formatDateTime(update.created_at)}</time>
                {newer && (
                  <span className="text-gray-400">
                    {' '}({formatSpan((new Date(newer.created_at).getTime() - new Date(update.created_at).getTime()) / 60_000)} earlier)
                  </span>
                )}
              </p>
            </li>
          )
        })}
      </ol>
    </section>
  )
}

export function StatusIncidentPage() {
  const { id } = useParams()
  const href = useStatusHref()
  const summary = useStatusBranding()
  const [incident, setIncident] = useState<StatusIncidentDetail | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setIncident(null)
    setError(null)
    window.fetch(`/api/v1/status/incidents/${encodeURIComponent(id ?? '')}`)
      .then(async res => {
        if (res.status === 404) throw new Error('This incident does not exist or is not public.')
        if (!res.ok) throw new Error('Unable to load this incident.')
        return res.json() as Promise<StatusIncidentDetail>
      })
      .then(json => { if (!cancelled) setIncident(json) })
      .catch((failure: Error) => { if (!cancelled) setError(failure.message) })
    return () => { cancelled = true }
  }, [id])

  const latest = incident?.updates[0]
  const headerTone = incident ? tone(incident) : 'resolved'
  return (
    <div className="min-h-screen bg-gray-50">
      <StatusHeader branding={summary?.branding} orgName={summary?.org_name} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {error && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-600">
            <p>{error}</p>
            <Link to={href('/')} className="mt-3 inline-block text-gray-900 underline">Back to status</Link>
          </div>
        )}
        {!incident && !error && <div className="h-40 bg-white rounded-lg border border-gray-200 animate-pulse" />}
        {incident && (
          <>
            <section className={`rounded-lg border-2 bg-white overflow-hidden ${HEADER_BORDER[headerTone]}`}>
              <div className={`flex items-start gap-2 px-4 sm:px-6 py-4 ${HEADER_BG[headerTone]}`}>
                <Link to={href('/')} aria-label="Back to status" className="mt-0.5 text-gray-500 hover:text-gray-900">
                  <ChevronLeft className="w-5 h-5" />
                </Link>
                <h2 className="text-lg font-semibold text-gray-900 break-words">{incident.title}</h2>
              </div>
              <p className="px-4 sm:px-6 py-3 text-sm text-gray-600 border-b border-gray-100">
                {incidentStateLabel(incident)} · {LEVEL_LABEL[incident.level]}
              </p>
              {latest ? (
                <div className="px-4 sm:px-6 py-4">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">{latest.message}</p>
                  <p className="mt-2 text-xs text-gray-500">
                    {formatDateTime(latest.created_at)} ({timeAgo(latest.created_at)})
                    {incident.updates.length > 1 && <> · <a href="#updates" className="hover:underline">View all updates</a></>}
                  </p>
                </div>
              ) : (
                <p className="px-4 sm:px-6 py-4 text-sm text-gray-600">
                  Started {formatDateTime(incident.triggered_at)}. No updates have been posted yet.
                </p>
              )}
            </section>
            <AffectedComponents incident={incident} />
            <Updates incident={incident} />
          </>
        )}
      </div>
    </div>
  )
}
